import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../Components/AuthContext';
import hasPermission from '../../../Hooks/hasPermission';
import { useTheme } from '../../theme/ThemeProvider';
import Input from '../../components/ui/Input/Input';
import Button from '../../components/ui/Button/Button';
import { MailIcon, LockIcon, EyeIcon, EyeOffIcon } from '../../components/ui/Icons';
import styles from './Login.module.css';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const resolveLandingRoute = (permissions: any[] | undefined): string => {
  // El gating de acceso (canAccess) se mantiene igual que en V1.
  // Por ahora todos los usuarios con acceso aterrizan en el dashboard (vista
  // principal del rediseño). TODO: afinar destinos (/loads, /rater) cuando
  // esas vistas se migren a V2.
  void permissions;
  return '/dashboard';
};

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as any)?.from?.pathname as string | undefined;

  const { login, isAuthenticated, user } = useAuth();
  const { resolvedTheme } = useTheme();

  const logoSrc =
    resolvedTheme === 'dark' ? '/dylo-logo-dark.svg' : '/dylo-logo-light.svg';

  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  // Si ya hay sesión activa, redirige fuera del login
  React.useEffect(() => {
    if (isAuthenticated && user) {
      navigate(resolveLandingRoute(user.permissionsActives), { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  /**
   * 🔄 21-oct-2026: el fondo del login también en el `<body>`, para que Safari pinte del mismo color la barra de estado
   * del iPhone (ver «El fondo del documento» en `styles/tokens.css`).
   */
  React.useEffect(() => {
    document.body.classList.add('dylo-login-shell');
    return () => document.body.classList.remove('dylo-login-shell');
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      const data = await login(email, password);
      const permissions = data?.user?.permissionsActives;

      const canAccess =
        hasPermission(permissions, 'LD_VIEW') ||
        hasPermission(permissions, 'US_VIEW') ||
        hasPermission(permissions, 'CL_VIEW') ||
        hasPermission(permissions, 'DL_VIEW') ||
        hasPermission(permissions, 'RATER');

      if (!canAccess) {
        setError('You do not have permission to access this application.');
        setLoading(false);
        return;
      }

      navigate(from || resolveLandingRoute(permissions), { replace: true });
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          'Incorrect credentials. Please try again.'
      );
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.cover}>
          <img
            className={styles.coverImg}
            src="/login-cover.png"
            alt="Dylo Dynamic Logistics"
          />
        </div>

        <div className={styles.panel}>
          <div className={styles.logoWrap}>
            <img className={styles.logo} src={logoSrc} alt="DYLO" />
          </div>

          <div className={styles.heading}>
            <h1 className={styles.title}>Welcome</h1>
            <p className={styles.subtitle}>Sign in to your account</p>
          </div>

          <form className={styles.form} onSubmit={onSubmit} noValidate>
            {error && (
              <div className={styles.error} role="alert">
                {error}
              </div>
            )}

            <Input
              type="email"
              placeholder="Email"
              autoComplete="username"
              icon={<MailIcon />}
              value={email}
              invalid={!!error}
              onChange={(e) => setEmail(e.target.value)}
            />

            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              autoComplete="current-password"
              icon={<LockIcon />}
              value={password}
              invalid={!!error}
              onChange={(e) => setPassword(e.target.value)}
              suffix={
                <button
                  type="button"
                  className={styles.suffix}
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              }
            />

            <Button
              type="submit"
              fullWidth
              loading={loading}
              className={styles.submit}
            >
              Log in
            </Button>
          </form>
        </div>
      </div>

      <p className={styles.footer}>
        Dylo Inc Dashboard ©2026 Created by Dylo Devs
      </p>
    </div>
  );
};

export default Login;
